import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { SearchableSelect } from '../components/SearchableSelect'; 
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Plus, Trash2, CreditCard, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';
import { ImportDialog } from '../components/ImportDialog';


export default function VirtualAccounts() {
  const [vas, setVas] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ supplier_id: '', company_id: '', nomor_va: '', nama_va: '' });
  const [filterQuery, setFilterQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [vaRes, supRes, compRes] = await Promise.all([
        api.get(`/virtual-accounts`),
        api.get(`/suppliers`),
        api.get(`/companies`)
      ]);
      setVas(vaRes.data);
      setSuppliers(supRes.data);
      setCompanies(compRes.data);
    } catch (e) { toast.error('Gagal memuat data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditId(null);
    setFormData({ supplier_id: '', company_id: '', nomor_va: '', nama_va: '' });
    setShowForm(true);
  };

  const openEdit = (va) => {
    setEditId(va.id);
    setFormData({ supplier_id: va.supplier_id, company_id: va.company_id, nomor_va: va.nomor_va, nama_va: va.nama_va });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id || !formData.company_id || !formData.nomor_va || !formData.nama_va) {
      return toast.error('Semua field wajib diisi');
    }
    setFormLoading(true);
    try {
      if (editId) {
        await api.put(`/virtual-accounts/${editId}`, formData);
        toast.success('VA berhasil diperbarui');
      } else {
        await api.post(`/virtual-accounts`, formData);
        toast.success('VA berhasil ditambahkan');
      }
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan');
    } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data VA ini?')) return;
    try {
      await api.delete(`/virtual-accounts/${id}`);
      toast.success('VA dihapus');
      fetchData();
    } catch (e) { toast.error('Gagal menghapus'); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div></Layout>;
  const q = filterQuery.trim().toLowerCase();
  const filteredVas = q
    ? vas.filter((va) =>
        (va.supplier_name || '').toLowerCase().includes(q) ||
        (va.company_name || '').toLowerCase().includes(q)
      )
    : vas;
  return (
    <Layout>
      <div className="space-y-6" data-testid="va-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Data Virtual Account</h1>
            <p className="text-slate-500 mt-1">Mapping virtual account per supplier dan company</p>
          </div>
          <div className="flex gap-2">
            <ExportMenu table="virtual-accounts" size="default" />
            <ImportDialog type="virtual-accounts" title="Data VA" size="default" onDone={fetchData} />
            <Button className="bg-red-600 hover:bg-red-700" onClick={openCreate} data-testid="add-va">
              <Plus className="h-4 w-4 mr-2" /> Tambah VA
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle>Daftar Virtual Account ({filteredVas.length}{q ? ` / ${vas.length}` : ''})</CardTitle></CardHeader>
          <CardContent>
            <div className="mb-6 pb-6 border-b flex items-end gap-3">
              <div className="space-y-2 flex-1">
                <Label className="text-xs text-slate-600">Filter Nama Supplier / Company</Label>
                <Input
                  placeholder="Cari nama supplier atau company..."
                  value={filterQuery}
                  onChange={(e) => setFilterQuery(e.target.value)}
                  data-testid="filter-va"
                />
              </div>
              {q && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setFilterQuery('')}
                  data-testid="clear-filter"
                >
                  Hapus Filter
                </Button>
              )}
            </div>

            {filteredVas.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-sm font-semibold">No</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama Supplier</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama Company</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nomor VA</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama VA</th>
                      <th className="px-4 py-3 text-sm font-semibold">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVas.map((va, idx) => (
                      <tr key={va.id} className="border-b" data-testid={`va-row-${va.id}`}>
                        <td className="px-4 py-3 text-sm text-slate-500">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium">{va.supplier_name}</td>
                        <td className="px-4 py-3">{va.company_name}</td>
                        <td className="px-4 py-3 font-mono font-semibold text-red-700">{va.nomor_va}</td>
                        <td className="px-4 py-3 font-semibold">{va.nama_va}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEdit(va)} data-testid={`edit-va-${va.id}`}><Pencil className="h-3 w-3" /></Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(va.id)} data-testid={`delete-va-${va.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <CreditCard className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>{q ? 'Tidak ada data yang cocok dengan filter' : 'Belum ada data Virtual Account'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editId ? 'Edit' : 'Tambah'} Virtual Account</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nama Supplier <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={suppliers}
                  value={formData.supplier_id}
                  onChange={(val) => setFormData({ ...formData, supplier_id: val })}
                  displayKey="nama_supplier"
                  valueKey="id"
                  placeholder="Ketik nama supplier..."
                  testId="va-supplier"
                 /> 
              </div>
              <div className="space-y-2">
                <Label>Nama Company <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={companies}
                  value={formData.company_id}
                  onChange={(val) => setFormData({ ...formData, company_id: val })}
                  displayKey="nama_perusahaan"
                  valueKey="id"
                  placeholder="Ketik nama company..."
                  testId="va-company"
                />
              </div>
              <div className="space-y-2">
                <Label>Nomor Virtual Account <span className="text-red-500">*</span></Label>
                <Input value={formData.nomor_va} onChange={(e) => setFormData({ ...formData, nomor_va: e.target.value })} placeholder="Masukkan nomor VA" className="font-mono" data-testid="va-nomor" />
              </div>
              <div className="space-y-2">
                <Label>Nama Virtual Account <span className="text-red-500">*</span></Label>
                <Input value={formData.nama_va} onChange={(e) => setFormData({ ...formData, nama_va: e.target.value.toUpperCase() })} placeholder="Nama pemilik VA" data-testid="va-nama" />
              </div>
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={formLoading} data-testid="va-submit">
                {formLoading ? 'Menyimpan...' : (editId ? 'Perbarui' : 'Tambah VA')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}



